/**
 * `/tv/{id}` details for the one show the sheet is showing.
 *
 * Shares the cache that the Library's bulk pass fills, so opening a show the
 * Library has already classified costs no request at all. A show the bulk pass
 * skipped — anything with episodes still to watch — is fetched here on open.
 *
 * Failure is silent: the sheet drops back to the header it had before any of
 * this existed rather than showing an error for supporting detail.
 */
import { useEffect, useState } from "react";

import type { SimklClient } from "@/api/client";
import { parseDetail, readOne, write } from "@/store/details";
import type { ShowDetail } from "@/store/details";

export function useShowDetail(client: SimklClient, simklId: number | null): ShowDetail | null {
  const [loaded, setLoaded] = useState<ShowDetail | null>(null);

  useEffect(() => {
    if (simklId === null) return;
    const controller = new AbortController();

    void (async () => {
      const cached = await readOne(simklId);
      if (controller.signal.aborted) return;
      if (cached !== null) {
        setLoaded(cached);
        return;
      }
      try {
        const detail = parseDetail(simklId, await client.show(simklId, controller.signal));
        if (controller.signal.aborted) return;
        setLoaded(detail);
        await write([detail]);
      } catch {
        // Supporting detail. The sheet reads fine without it.
      }
    })();

    return () => {
      controller.abort();
    };
  }, [client, simklId]);

  // Tagged with its show, so one show's rating can never appear under another's
  // title while the next request is in flight.
  return loaded !== null && loaded.simklId === simklId ? loaded : null;
}
